import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import Layout from "../components/Layout";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { ArrowLeft, ShieldCheck, Sparkles, Trash2, Plus } from "lucide-react";
import { toast } from "sonner";

export default function KeyDictionary() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [popular, setPopular] = useState([]);
  const [canonical, setCanonical] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    const [pop, dict] = await Promise.all([
      api.get("/forms/popular-keys"),
      api.get("/key-dictionary"),
    ]);
    setPopular(pop.data || []);
    setCanonical(dict.data || []);
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const canonicalSet = useMemo(() => new Set(canonical.map((c) => c.key)), [canonical]);
  const popularPending = useMemo(
    () => popular.filter((p) => !canonicalSet.has(p.key)),
    [popular, canonicalSet]
  );

  const promote = async (entry) => {
    try {
      await api.post("/key-dictionary", {
        key: entry.key,
        label: (entry.labels && entry.labels[0]) || entry.label || entry.key,
        description: "",
      });
      toast.success(`${entry.key} promoted to canonical`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not promote");
    }
  };

  const remove = async (key) => {
    try {
      await api.delete(`/key-dictionary/${key}`);
      toast.success(`${key} removed from dictionary`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not remove");
    }
  };

  return (
    <Layout>
      <div className="mb-6">
        <Link to="/" className="inline-flex items-center text-xs text-slate-500 hover:text-slate-900">
          <ArrowLeft className="w-3 h-3 mr-1" /> All programs
        </Link>
        <p className="label-overline mt-3">Workspace</p>
        <h1 className="font-heading text-4xl sm:text-5xl tracking-tight font-semibold mt-1">Key dictionary</h1>
        <p className="text-slate-600 mt-2 max-w-2xl">
          Promote keys that should be the workspace standard. Promoted keys appear first in every form
          builder's suggestion dropdown with a black <ShieldCheck className="w-3 h-3 inline -mt-0.5" /> Canonical badge.
        </p>
        {!isAdmin && (
          <p className="mt-3 text-xs text-amber-700 bg-amber-50 border border-amber-200 inline-block px-2 py-1 rounded-sm">
            Admin role required to promote or remove canonical keys.
          </p>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <section data-testid="canonical-keys-section">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-heading text-2xl font-semibold">Canonical keys ({canonical.length})</h2>
            {isAdmin && <ManualAddDialog onSaved={load} />}
          </div>
          {loading ? (
            <p className="text-sm text-slate-500">Loading…</p>
          ) : canonical.length === 0 ? (
            <div className="border border-dashed border-slate-300 bg-white p-6 text-sm text-slate-500" data-testid="canonical-empty">
              No canonical keys yet. Promote a popular key on the right, or add one manually.
            </div>
          ) : (
            <ul className="space-y-2">
              {canonical.map((c) => (
                <li
                  key={c.key}
                  className="bg-white border border-slate-200 rounded-sm p-3 flex items-start gap-3"
                  data-testid={`canonical-${c.key}`}
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-mono text-sm font-medium">{c.key}</span>
                      <span className="bg-slate-900 text-white text-[10px] px-1.5 py-0.5 rounded-sm uppercase tracking-wider inline-flex items-center gap-1">
                        <ShieldCheck className="w-2.5 h-2.5" /> Canonical
                      </span>
                    </div>
                    <p className="text-xs text-slate-700 mt-1">{c.label}</p>
                    {c.description && <p className="text-[11px] text-slate-500 mt-1">{c.description}</p>}
                    <p className="text-[11px] text-slate-400 mt-1">
                      Promoted by {c.promoted_by_name || "?"} · {c.promoted_at ? new Date(c.promoted_at).toLocaleDateString() : ""}
                    </p>
                  </div>
                  {isAdmin && (
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => remove(c.key)}
                      data-testid={`remove-canonical-${c.key}`}
                      title="Remove from dictionary"
                    >
                      <Trash2 className="w-4 h-4 text-slate-500" />
                    </Button>
                  )}
                </li>
              ))}
            </ul>
          )}
        </section>

        <section data-testid="popular-keys-section">
          <div className="flex items-center mb-3 gap-2">
            <h2 className="font-heading text-2xl font-semibold">Popular keys ({popularPending.length})</h2>
            <Sparkles className="w-4 h-4 text-emerald-600" />
          </div>
          {popularPending.length === 0 ? (
            <div className="border border-dashed border-slate-300 bg-white p-6 text-sm text-slate-500">
              No keys appear in 2+ published forms yet, or every popular key is already canonical.
            </div>
          ) : (
            <ul className="space-y-2">
              {popularPending.map((p) => (
                <li
                  key={p.key}
                  className="bg-white border border-slate-200 rounded-sm p-3 flex items-start gap-3"
                  data-testid={`popular-${p.key}`}
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-mono text-sm font-medium">{p.key}</span>
                      <span className="bg-emerald-50 text-emerald-700 border border-emerald-200 text-[10px] px-1.5 py-0.5 rounded-sm uppercase tracking-wider inline-flex items-center gap-1">
                        <Sparkles className="w-2.5 h-2.5" /> Used in {p.count} form{p.count === 1 ? "" : "s"}
                      </span>
                    </div>
                    {(p.labels || []).length > 0 && (
                      <p className="text-xs text-slate-600 mt-1">As: {(p.labels || []).slice(0, 4).join(" · ")}</p>
                    )}
                  </div>
                  {isAdmin && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => promote(p)}
                      data-testid={`promote-${p.key}`}
                    >
                      Promote
                    </Button>
                  )}
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>
    </Layout>
  );
}

function ManualAddDialog({ onSaved }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ key: "", label: "", description: "" });
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/key-dictionary", {
        key: form.key,
        label: form.label,
        description: form.description,
      });
      toast.success(`${form.key} added`);
      setForm({ key: "", label: "", description: "" });
      setOpen(false);
      onSaved?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || err.response?.data?.[0]?.msg || "Could not save");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" variant="outline" data-testid="add-canonical-btn">
          <Plus className="w-3 h-3 mr-1" /> Add
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Add a canonical key</DialogTitle>
          <DialogDescription>Manually define a workspace standard, even if no published form uses it yet.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3 text-sm">
          <div>
            <Label className="label-overline">Key</Label>
            <Input
              value={form.key}
              onChange={(e) => setForm({ ...form, key: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, "_") })}
              required
              placeholder="phone_number"
              className="mt-1 font-mono"
              data-testid="add-canonical-key"
            />
          </div>
          <div>
            <Label className="label-overline">Label</Label>
            <Input
              value={form.label}
              onChange={(e) => setForm({ ...form, label: e.target.value })}
              required
              placeholder="Phone number"
              className="mt-1"
              data-testid="add-canonical-label"
            />
          </div>
          <div>
            <Label className="label-overline">Description (optional)</Label>
            <Textarea
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              placeholder="When and how this key should be used"
              className="mt-1"
              rows={2}
              data-testid="add-canonical-description"
            />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setOpen(false)} disabled={saving}>Cancel</Button>
            <Button type="submit" className="bg-slate-900 hover:bg-slate-800 text-white" disabled={saving} data-testid="add-canonical-submit">
              {saving ? "Saving…" : "Add"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
